import { memo, useCallback, useMemo, useState } from "react"
import { Badge, Box, Button, HStack, Input, Link, Spinner, Stack, Switch, Table, Tabs } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { Section, Text } from "@/components"
import { getStrandedValueDeals } from "@/api/strandedValue"
import { computeEsoReturn, computeEsoLtv, findBreakevenPp } from "@/utils/returnCalculation"

// ── Types ──────────────────────────────────────────────────────

type SignalType = "exit" | "secondary" | "409a" | "fundraising" | "none"

interface TopSignal {
  type: SignalType
  detail: string | null
  referencePrice: number
  priceSource: string
  priceDate: string | null
}

interface StrandedDeal {
  dealId: number
  dealIdStr: string | null
  companyName: string
  companyId: number
  passType: string
  dealManager: string | null
  grants: { grantType: string; vested: number; strikePrice: number }[]
  totalSharesPledged: number
  weightedAvgStrike: number
  referencePrice: number
  priceDate: string | null
  existingOffer: { pp: number; ppSplit: number; allocationAgreement: string } | null
  exerciseCost: number
  grossProceeds: number
  inTheMoney: boolean
  signal: TopSignal
}

type SortField = "companyName" | "signal" | "signalPrice" | "ltv" | "exerciseCost" | "grossProceeds" | "spread" | "esoProfit" | "esoRoi" | "breakevenPp"
type SortDir = "asc" | "desc"

// ── Defaults ───────────────────────────────────────────────────

const FRIENDLY_PP = 0.15
const FRIENDLY_PP_SPLIT = 1.0
const PAGE_SIZE = 100

const SIGNAL_RANK: Record<SignalType, number> = { exit: 4, secondary: 3, "409a": 2, fundraising: 1, none: 0 }
const SIGNAL_COLOR: Record<SignalType, string> = { exit: "red", secondary: "purple", "409a": "blue", fundraising: "teal", none: "gray" }
const SIGNAL_LABEL: Record<SignalType, string> = { exit: "Exit", secondary: "Secondary", "409a": "409A", fundraising: "Fundraising", none: "None" }

// ── Helpers ────────────────────────────────────────────────────

const fmtUsd = (n: number) => {
  if (Math.abs(n) >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}K`
  return `$${n.toFixed(2)}`
}

const fmtPct = (n: number) => `${(n * 100).toFixed(1)}%`

const fmtRoi = (n: number | null) => (n != null ? `${n.toFixed(2)}x` : "—")

const DEFAULT_SIGNAL: TopSignal = { type: "none", detail: null, referencePrice: 0, priceSource: "Unknown", priceDate: null }

// ── Component ──────────────────────────────────────────────────

const PASS_TYPES = ["ESO Passed", "Client Passed", "Robo Reject"] as const

export const StrandedValueComponent = () => {
  const { data: deals, isLoading } = useQuery<StrandedDeal[]>({
    queryKey: ["strandedValue"],
    queryFn: getStrandedValueDeals,
    staleTime: 5 * 60 * 1000,
  })

  // Global simulation state
  const [useFriendlyTerms, setUseFriendlyTerms] = useState(true)
  const [customPp, setCustomPp] = useState("15")
  const [customPpSplit, setCustomPpSplit] = useState("100")

  // Sort state — default to signal priority
  const [sortField, setSortField] = useState<SortField>("signal")
  const [sortDir, setSortDir] = useState<SortDir>("desc")

  // Signal type filter
  const [signalFilter, setSignalFilter] = useState<SignalType | "all">("all")

  // Expanded row
  const [expandedDealId, setExpandedDealId] = useState<number | null>(null)

  // Active terms
  const activePp = useFriendlyTerms ? FRIENDLY_PP : (parseFloat(customPp) || 15) / 100
  const activePpSplit = useFriendlyTerms ? FRIENDLY_PP_SPLIT : (parseFloat(customPpSplit) || 100) / 100

  // Compute returns for all deals
  const enrichedDeals = useMemo(() => {
    if (!deals) return []
    return deals
      .map((d) => ({ ...d, signal: d.signal ?? DEFAULT_SIGNAL }))
      .filter((d) => d.inTheMoney)
      .map((deal) => {
        const pp = deal.existingOffer ? deal.existingOffer.pp : activePp
        const ppSplit = deal.existingOffer ? deal.existingOffer.ppSplit : activePpSplit
        const aa = deal.existingOffer?.allocationAgreement ?? "PPA"

        const result = computeEsoReturn({
          exitPricePerShare: deal.referencePrice,
          totalSharesPledged: deal.totalSharesPledged,
          pp,
          ppSplit,
          totalUpfrontPayment: deal.exerciseCost,
          allocationAgreement: aa,
        })

        const breakevenPp = findBreakevenPp(
          deal.referencePrice,
          deal.totalSharesPledged,
          deal.exerciseCost,
          aa,
        )

        const ltv = computeEsoLtv(deal.exerciseCost, deal.totalSharesPledged, pp, aa)

        return {
          ...deal,
          usedPp: pp,
          usedPpSplit: ppSplit,
          computedLtv: ltv,
          spread: deal.referencePrice - ltv,
          ...result,
          breakevenPp,
        }
      })
  }, [deals, activePp, activePpSplit])

  // Apply signal filter
  const filteredDeals = useMemo(() => {
    if (signalFilter === "all") return enrichedDeals
    return enrichedDeals.filter((d) => d.signal.type === signalFilter)
  }, [enrichedDeals, signalFilter])

  // Signal counts for filter chips
  const signalCounts = useMemo(() => {
    const counts: Record<string, number> = { all: enrichedDeals.length, exit: 0, secondary: 0, "409a": 0, fundraising: 0, none: 0 }
    for (const d of enrichedDeals) counts[d.signal.type] = (counts[d.signal.type] || 0) + 1
    return counts
  }, [enrichedDeals])

  // Split by pass type
  const dealsByTab = useMemo(() => {
    const map: Record<string, typeof filteredDeals> = {}
    for (const pt of PASS_TYPES) map[pt] = []
    for (const d of filteredDeals) {
      if (map[d.passType]) map[d.passType].push(d)
    }
    return map
  }, [filteredDeals])

  // Sort helper
  const sortFn = useCallback((list: EnrichedDeal[]) => {
    return [...list].sort((a, b) => {
      let av: number, bv: number
      switch (sortField) {
        case "companyName": return sortDir === "asc" ? a.companyName.localeCompare(b.companyName) : b.companyName.localeCompare(a.companyName)
        case "signal": av = SIGNAL_RANK[a.signal.type]; bv = SIGNAL_RANK[b.signal.type]; break
        case "signalPrice": av = a.referencePrice; bv = b.referencePrice; break
        case "ltv": av = a.computedLtv; bv = b.computedLtv; break
        case "exerciseCost": av = a.exerciseCost; bv = b.exerciseCost; break
        case "grossProceeds": av = a.grossProceeds; bv = b.grossProceeds; break
        case "spread": av = a.spread; bv = b.spread; break
        case "esoProfit": av = a.esoProfit; bv = b.esoProfit; break
        case "esoRoi": av = a.esoRoi ?? 0; bv = b.esoRoi ?? 0; break
        case "breakevenPp": av = a.breakevenPp ?? 1; bv = b.breakevenPp ?? 1; break
        default: av = 0; bv = 0
      }
      return sortDir === "asc" ? av - bv : bv - av
    })
  }, [sortField, sortDir])

  const handleSort = useCallback((field: SortField) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"))
        return prev
      }
      setSortDir("desc")
      return field
    })
  }, [])

  const sortIndicator = useCallback((field: SortField) => (sortField === field ? (sortDir === "asc" ? " ▲" : " ▼") : ""), [sortField, sortDir])

  const handleToggleExpand = useCallback((id: number) => {
    setExpandedDealId((prev) => (prev === id ? null : id))
  }, [])

  // Summary stats
  const totalDeals = filteredDeals.length
  const totalExerciseCost = filteredDeals.reduce((s, d) => s + d.exerciseCost, 0)
  const totalGross = filteredDeals.reduce((s, d) => s + d.grossProceeds, 0)
  const totalEsoProfit = filteredDeals.reduce((s, d) => s + d.esoProfit, 0)

  if (isLoading) {
    return (
      <Section>
        <HStack gap={3} py={10} justifyContent="center">
          <Spinner size="lg" />
          <Text>Loading stranded value analysis...</Text>
        </HStack>
      </Section>
    )
  }

  return (
    <Section>
      <Stack gap={4}>
        {/* Header */}
        <Text fontSize="2xl" fontWeight="bold" color="text.primary">
          Stranded Value Analysis
        </Text>
        <Text fontSize="sm" color="text.muted">
          Passed deals where the reference price exceeds the client's strike price. Price sourced from the highest-priority signal: Exit &gt; Secondary &gt; 409A &gt; Fundraising.
        </Text>

        {/* Summary stats */}
        <HStack gap={6} flexWrap="wrap">
          <StatCard label="Matching Deals" value={totalDeals.toLocaleString()} />
          <StatCard label="Total Exercise Cost" value={fmtUsd(totalExerciseCost)} />
          <StatCard label="Total Gross Value" value={fmtUsd(totalGross)} />
          <StatCard label="Total Simulated ESO Profit" value={fmtUsd(totalEsoProfit)} />
        </HStack>

        {/* Signal Filter */}
        <HStack gap={2} flexWrap="wrap">
          <Text fontSize="sm" fontWeight="medium" color="text.primary">Signal:</Text>
          <Button
            size="xs"
            variant={signalFilter === "all" ? "solid" : "outline"}
            onClick={() => setSignalFilter("all")}
          >
            All ({signalCounts.all})
          </Button>
          {(["exit", "secondary", "409a", "fundraising"] as SignalType[]).map((t) => (
            <Button
              key={t}
              size="xs"
              variant={signalFilter === t ? "solid" : "outline"}
              colorPalette={SIGNAL_COLOR[t]}
              onClick={() => setSignalFilter(signalFilter === t ? "all" : t)}
            >
              {SIGNAL_LABEL[t]} ({signalCounts[t] || 0})
            </Button>
          ))}
        </HStack>

        {/* Terms Controls */}
        <HStack gap={4} flexWrap="wrap" p={3} bg="card.bg" borderRadius="md" borderWidth="1px" borderColor="border.default">
          <HStack gap={2}>
            <Text fontSize="sm" fontWeight="medium">Friendly Terms (15% PP, 1x LP)</Text>
            <Switch.Root
              checked={useFriendlyTerms}
              onCheckedChange={(e) => setUseFriendlyTerms(e.checked)}
              size="sm"
            >
              <Switch.HiddenInput />
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
            </Switch.Root>
          </HStack>
          {!useFriendlyTerms && (
            <HStack gap={2}>
              <Text fontSize="sm">PP%:</Text>
              <Input size="sm" w="70px" value={customPp} onChange={(e) => setCustomPp(e.target.value)} />
              <Text fontSize="sm">PP Split%:</Text>
              <Input size="sm" w="70px" value={customPpSplit} onChange={(e) => setCustomPpSplit(e.target.value)} />
            </HStack>
          )}
          <Text fontSize="xs" color="text.muted">
            Deals with existing offers use their original terms. Simulation applies to deals without offers.
          </Text>
        </HStack>

        {/* Tabs */}
        <Tabs.Root defaultValue="ESO Passed" variant="line">
          <Tabs.List borderColor="border.default">
            {PASS_TYPES.map((pt) => (
              <Tabs.Trigger key={pt} value={pt}>
                {pt} ({(dealsByTab[pt] || []).length})
              </Tabs.Trigger>
            ))}
          </Tabs.List>

          {PASS_TYPES.map((pt) => (
            <Tabs.Content key={pt} value={pt}>
              <DealTable
                deals={dealsByTab[pt] || []}
                sortFn={sortFn}
                onSort={handleSort}
                sortIndicator={sortIndicator}
                expandedDealId={expandedDealId}
                onToggleExpand={handleToggleExpand}
              />
              <TabSummary deals={dealsByTab[pt] || []} />
            </Tabs.Content>
          ))}
        </Tabs.Root>
      </Stack>
    </Section>
  )
}

// ── Sub-Components ─────────────────────────────────────────────

const StatCard = ({ label, value }: { label: string; value: string }) => (
  <Box p={3} bg="card.bg" borderRadius="md" borderWidth="1px" borderColor="border.default" minW="160px">
    <Text fontSize="xs" color="text.muted">{label}</Text>
    <Text fontSize="lg" fontWeight="bold" color="text.primary">{value}</Text>
  </Box>
)

interface EnrichedDeal extends StrandedDeal {
  usedPp: number
  usedPpSplit: number
  computedLtv: number
  spread: number
  grossProceeds: number
  computedLp: number
  esoProceeds: number
  esoProfit: number
  esoRoi: number | null
  optioneeNet: number
  breakevenPp: number | null
}

const DealTable = ({
  deals,
  sortFn,
  onSort,
  sortIndicator,
  expandedDealId,
  onToggleExpand,
}: {
  deals: EnrichedDeal[]
  sortFn: (list: EnrichedDeal[]) => EnrichedDeal[]
  onSort: (f: SortField) => void
  sortIndicator: (f: SortField) => string
  expandedDealId: number | null
  onToggleExpand: (id: number) => void
}) => {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  const sorted = useMemo(() => sortFn(deals), [sortFn, deals])
  const visible = useMemo(() => sorted.slice(0, visibleCount), [sorted, visibleCount])
  const hasMore = visibleCount < sorted.length

  if (deals.length === 0) {
    return <Text py={6} color="text.muted" textAlign="center">No in-the-money deals in this category.</Text>
  }

  return (
    <Stack gap={2} mt={2}>
      <Box overflowX="auto">
        <Table.Root size="sm" variant="outline">
          <Table.Header>
            <Table.Row bg="table.header">
              <Table.ColumnHeader cursor="pointer" onClick={() => onSort("signal")} textAlign="center" w="90px">Signal{sortIndicator("signal")}</Table.ColumnHeader>
              <Table.ColumnHeader cursor="pointer" onClick={() => onSort("companyName")}>Company{sortIndicator("companyName")}</Table.ColumnHeader>
              <Table.ColumnHeader cursor="pointer" onClick={() => onSort("signalPrice")} textAlign="right">Signal Price{sortIndicator("signalPrice")}</Table.ColumnHeader>
              <Table.ColumnHeader cursor="pointer" onClick={() => onSort("ltv")} textAlign="right">LTV{sortIndicator("ltv")}</Table.ColumnHeader>
              <Table.ColumnHeader cursor="pointer" onClick={() => onSort("spread")} textAlign="right">Spread{sortIndicator("spread")}</Table.ColumnHeader>
              <Table.ColumnHeader cursor="pointer" onClick={() => onSort("exerciseCost")} textAlign="right">Exercise Cost{sortIndicator("exerciseCost")}</Table.ColumnHeader>
              <Table.ColumnHeader cursor="pointer" onClick={() => onSort("grossProceeds")} textAlign="right">Gross Value{sortIndicator("grossProceeds")}</Table.ColumnHeader>
              <Table.ColumnHeader cursor="pointer" onClick={() => onSort("esoProfit")} textAlign="right">ESO Profit{sortIndicator("esoProfit")}</Table.ColumnHeader>
              <Table.ColumnHeader cursor="pointer" onClick={() => onSort("esoRoi")} textAlign="right">ROI{sortIndicator("esoRoi")}</Table.ColumnHeader>
              <Table.ColumnHeader cursor="pointer" onClick={() => onSort("breakevenPp")} textAlign="right">BE PP{sortIndicator("breakevenPp")}</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="center">Terms</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {visible.map((deal) => (
              <DealRow
                key={deal.dealId}
                deal={deal}
                isExpanded={expandedDealId === deal.dealId}
                onToggle={onToggleExpand}
              />
            ))}
          </Table.Body>
        </Table.Root>
      </Box>
      {hasMore && (
        <HStack justifyContent="center">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
          >
            Show more ({sorted.length - visibleCount} remaining)
          </Button>
        </HStack>
      )}
    </Stack>
  )
}

const DealRow = memo(({ deal, isExpanded, onToggle }: {
  deal: EnrichedDeal
  isExpanded: boolean
  onToggle: (id: number) => void
}) => (
  <>
    <Table.Row
      cursor="pointer"
      _hover={{ bg: "hover.bg" }}
      onClick={() => onToggle(deal.dealId)}
    >
      <Table.Cell textAlign="center">
        <Badge
          colorPalette={SIGNAL_COLOR[deal.signal.type]}
          size="sm"
          title={deal.signal.detail || ""}
        >
          {SIGNAL_LABEL[deal.signal.type]}
        </Badge>
      </Table.Cell>
      <Table.Cell fontWeight="medium">{deal.companyName}</Table.Cell>
      <Table.Cell textAlign="right">${deal.referencePrice.toFixed(2)}</Table.Cell>
      <Table.Cell textAlign="right">${deal.computedLtv.toFixed(2)}</Table.Cell>
      <Table.Cell textAlign="right" color={deal.spread > 0 ? "green.500" : "red.500"}>
        ${deal.spread.toFixed(2)}
      </Table.Cell>
      <Table.Cell textAlign="right">{fmtUsd(deal.exerciseCost)}</Table.Cell>
      <Table.Cell textAlign="right">{fmtUsd(deal.grossProceeds)}</Table.Cell>
      <Table.Cell textAlign="right" color={deal.esoProfit > 0 ? "green.500" : "red.500"}>
        {fmtUsd(deal.esoProfit)}
      </Table.Cell>
      <Table.Cell textAlign="right">{fmtRoi(deal.esoRoi)}</Table.Cell>
      <Table.Cell textAlign="right">{deal.breakevenPp != null ? fmtPct(deal.breakevenPp) : "—"}</Table.Cell>
      <Table.Cell textAlign="center">
        {deal.existingOffer ? (
          <Badge colorPalette="green" size="sm">Offer</Badge>
        ) : (
          <Badge colorPalette="gray" size="sm">Sim</Badge>
        )}
      </Table.Cell>
    </Table.Row>
    {isExpanded && (
      <Table.Row>
        <Table.Cell colSpan={11} p={0}>
          <DealDetail deal={deal} />
        </Table.Cell>
      </Table.Row>
    )}
  </>
))

const DealDetail = ({ deal }: { deal: EnrichedDeal }) => (
  <Box p={4} bg="card.bg" borderBottomWidth="1px" borderColor="border.default">
    <HStack gap={8} flexWrap="wrap" alignItems="flex-start">
      {/* Signal detail */}
      <Stack gap={1}>
        <Text fontSize="xs" fontWeight="bold" color="text.muted">SIGNAL</Text>
        <HStack gap={1}>
          <Badge colorPalette={SIGNAL_COLOR[deal.signal.type]} size="sm">{SIGNAL_LABEL[deal.signal.type]}</Badge>
        </HStack>
        {deal.signal.detail && <Text fontSize="xs">{deal.signal.detail}</Text>}
        <Text fontSize="xs" color="text.muted">
          Price: ${deal.referencePrice.toFixed(2)} ({deal.signal.priceSource})
        </Text>
      </Stack>

      {/* Grants */}
      <Stack gap={1}>
        <Text fontSize="xs" fontWeight="bold" color="text.muted">GRANTS</Text>
        {deal.grants.map((g, i) => (
          <Text key={i} fontSize="xs">
            {g.grantType.toUpperCase()}: {g.vested.toLocaleString()} @ ${g.strikePrice.toFixed(2)}
          </Text>
        ))}
      </Stack>

      {/* Pricing */}
      <Stack gap={1}>
        <Text fontSize="xs" fontWeight="bold" color="text.muted">PRICING</Text>
        <Text fontSize="xs">LTV: ${deal.computedLtv.toFixed(2)}/share</Text>
        <Text fontSize="xs">Avg Strike: ${deal.weightedAvgStrike.toFixed(2)}</Text>
        <Text fontSize="xs">Signal Price: ${deal.referencePrice.toFixed(2)}</Text>
        <Text fontSize="xs" color={deal.spread > 0 ? "green.500" : "red.500"}>
          Spread (Signal - LTV): ${deal.spread.toFixed(2)}
        </Text>
        <Text fontSize="xs">Shares: {deal.totalSharesPledged.toLocaleString()}</Text>
      </Stack>

      {/* Terms used */}
      <Stack gap={1}>
        <Text fontSize="xs" fontWeight="bold" color="text.muted">TERMS USED</Text>
        <Text fontSize="xs">PP: {fmtPct(deal.usedPp)}</Text>
        <Text fontSize="xs">PP Split: {fmtPct(deal.usedPpSplit)}</Text>
        <Text fontSize="xs">LP: {fmtUsd(deal.computedLp)}</Text>
        <Text fontSize="xs">
          Source: {deal.existingOffer ? "Existing Offer" : "Simulated"}
        </Text>
      </Stack>

      {/* Return breakdown */}
      <Stack gap={1}>
        <Text fontSize="xs" fontWeight="bold" color="text.muted">RETURN BREAKDOWN</Text>
        <Text fontSize="xs">Gross Proceeds: {fmtUsd(deal.grossProceeds)}</Text>
        <Text fontSize="xs">ESO Proceeds: {fmtUsd(deal.esoProceeds)}</Text>
        <Text fontSize="xs">ESO Profit: {fmtUsd(deal.esoProfit)}</Text>
        <Text fontSize="xs">Optionee Net: {fmtUsd(deal.optioneeNet)}</Text>
      </Stack>

      {/* Deal info */}
      <Stack gap={1}>
        <Text fontSize="xs" fontWeight="bold" color="text.muted">DEAL INFO</Text>
        <Text fontSize="xs">
          Deal ID:{" "}
          <Link
            href={`/core/deal/${deal.dealId}`}
            target="_blank"
            color="blue.500"
            fontWeight="medium"
            onClick={(e) => e.stopPropagation()}
          >
            {deal.dealIdStr || deal.dealId}
          </Link>
        </Text>
        <Text fontSize="xs">Manager: {deal.dealManager || "—"}</Text>
        <Text fontSize="xs">Price Date: {deal.priceDate || "—"}</Text>
        <Text fontSize="xs">Breakeven PP: {deal.breakevenPp != null ? fmtPct(deal.breakevenPp) : "—"}</Text>
      </Stack>
    </HStack>
  </Box>
)

const TabSummary = ({ deals }: { deals: EnrichedDeal[] }) => {
  if (deals.length === 0) return null
  const totalExCost = deals.reduce((s, d) => s + d.exerciseCost, 0)
  const totalGross = deals.reduce((s, d) => s + d.grossProceeds, 0)
  const totalProfit = deals.reduce((s, d) => s + d.esoProfit, 0)

  return (
    <HStack gap={6} mt={3} p={3} bg="card.bg" borderRadius="md" borderWidth="1px" borderColor="border.default" flexWrap="wrap">
      <Text fontSize="sm"><strong>{deals.length}</strong> deals</Text>
      <Text fontSize="sm">Exercise Cost: <strong>{fmtUsd(totalExCost)}</strong></Text>
      <Text fontSize="sm">Gross Value: <strong>{fmtUsd(totalGross)}</strong></Text>
      <Text fontSize="sm" color={totalProfit > 0 ? "green.500" : "red.500"}>
        Simulated ESO Profit: <strong>{fmtUsd(totalProfit)}</strong>
      </Text>
    </HStack>
  )
}
